import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { BiEdit } from 'react-icons/bi';
import { MdDelete } from 'react-icons/md';
import { PiDotsThree } from 'react-icons/pi';
import Pagination from '../components/Pagination';
import { toast } from 'react-toastify';
import { FaDice, FaEye, FaEyeSlash, FaCopy } from 'react-icons/fa';
import { generatePassword } from '../utils/generatePassword';

const AddUsers = () => {
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    courier_id: '',
  });

  const [metaData, setMetaData] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 10,
  });

  const [couriers, setCouriers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
  const apiUrl = 'process.env.REACT_APP_BASE_URL';

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/user`);
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      setUsers(data.data);
      setMetaData(data.meta);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCouriers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/courier/all`);
      if (!response.ok) {
        throw new Error('Failed to fetch couriers');
      }
      const data = await response.json();
      setCouriers(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/roles`);
      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }
      const data = await response.json();
      setRoles(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword(12); // Generate a 12-character password
    setUserData({ ...userData, password: newPassword });
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(userData.password);
    toast.success('Password copied to clipboard');
  };

  const handleEmail = async () => {
    const emailContent = `
      <html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Welcome to Zamar Freight</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            background-color: #f5f5f5;
            color: #333;
        }
        .email-container {
            max-width: 600px;
            margin: 20px auto;
            background: #fff;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            border: 1px solid #eaeaea;
        }
        .header {
            background-color: #007bff;
            color: #fff;
            text-align: center;
            padding: 20px;
        }
        .header h1 {
            margin: 0;
        }
        .content {
            padding: 20px;
        }
        .account-details {
            background-color: #f9f9f9;
            padding: 15px;
            border: 1px solid #ddd;
            border-radius: 5px;
            margin-top: 20px;
        }
        .account-details h3 {
            margin-top: 0;
        }
        .footer {
            background-color: #f1f1f1;
            color: #888;
            text-align: center;
            padding: 10px;
            font-size: 14px;
        }
        .footer a {
            color: #007bff;
            text-decoration: none;
        }
    </style>
</head>
<body>
    <div class="email-container">
        <div class="header">
            <h1>Welcome to Zamar Freight!</h1>
        </div>
        <div class="content">
            <p>Dear ${userData.first_name} ${userData.last_name},</p>
            <p>We’re excited to have you on board! Your account has been successfully created and you’re now ready to start using our services.</p>
            <div class="account-details">
                <h3>Your Account Details:</h3>
                <p><strong>Email:</strong> ${userData.email}</p>
                <p><strong>Password:</strong> ${userData.password}</p>


                <p><strong>Please Visit the wrehouse to get started: </strong>https://z3plwarehousing.com/warehouse/courier</p>
            </div>
            <p>Thank you for choosing Zamar Freight!</p>
            <p>Best Regards,<br>The Zamar Freight Team</p>
        </div>
        <div class="footer">
            <p>&copy; ${new Date().getFullYear()} Zamar Freight. All rights reserved.</p>
        </div>
    </div>
</body>
</html>
`;
    try {
      const response = await fetch(`${apiUrl}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: userData.email,
          subject: 'Welcome to Zamar Freight!',
          html: emailContent,
        }),
      });
      if (response.ok) {
        toast.success('Email sent successfully');
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      toast.error(error.message || 'Error sending email');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCouriers();
    fetchRoles();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await fetch(`${apiUrl}/api/user/${currentUserId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        });
      } else {
        await fetch(`${apiUrl}/api/user/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        });
      }
      if (!editMode) await handleEmail();
      fetchUsers();
      setModalOpen(false);
      setUserData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        courier_id: '',
      });
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Error saving user. Please try again.');
    }
  };

  const handleEdit = (id) => {
    const toEdit = users.find((user) => user.id === id);
    if (toEdit) {
      setUserData(toEdit);
      setCurrentUserId(id);
      setEditMode(true);
      setModalOpen(true);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${apiUrl}/api/user/${id}`, {
        method: 'DELETE',
      });
      fetchUsers();
      setModalDeleteOpen(false);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Error deleting user. Please try again.');
    }
  };

  const sampleData = [
    {
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com",
      "courier_username": "FastExpress"
    },
    {
      "first_name": "Jane",
      "last_name": "Smith",
      "email": "jane.smith@example.com",
      "courier_username": "QuickShip"
    },
    {
      "first_name": "Michael",
      "last_name": "Brown",
      "email": "michael.brown@example.com",
      "courier_username": "SpeedyLogistics"
    },
    {
      "first_name": "Emily",
      "last_name": "Davis",
      "email": "emily.davis@example.com",
      "courier_username": "ExpressTrack"
    },
    {
      "first_name": "David",
      "last_name": "Wilson",
      "email": "david.wilson@example.com",
      "courier_username": "SwiftDeliveries"
    },
    {
      "first_name": "Sophia",
      "last_name": "Martinez",
      "email": "sophia.martinez@example.com",
      "courier_username": "FastLaneShipping"
    },
    {
      "first_name": "Daniel",
      "last_name": "Taylor",
      "email": "daniel.taylor@example.com",
      "courier_username": "ParcelPro"
    },
    {
      "first_name": "Olivia",
      "last_name": "Anderson",
      "email": "olivia.anderson@example.com",
      "courier_username": "ReliableCouriers"
    },
    {
      "first_name": "Liam",
      "last_name": "Thomas",
      "email": "liam.thomas@example.com",
      "courier_username": "AirSwift"
    },
    {
      "first_name": "Emma",
      "last_name": "Harris",
      "email": "emma.harris@example.com",
      "courier_username": "GlobalFreight"
    }
  ]
  
  
  return (
    <Layout>
      <div className="card lg:h-full w-4/5 lg:w-full  p-6 bg-base-100 shadow-xl">
        <section className="flex justify-between items-center mb-4">
          <h2 className="text-3xl font-semibold">Users</h2>
          <button className="btn btn-error text-white" onClick={() => setModalOpen(true)}>
            Add User
          </button>
        </section>
        <section>
          <table className="table-auto w-full border-collapse border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2">First Name</th>
                <th className="border px-4 py-2">Last Name</th>
                <th className="border px-4 py-2">Email</th>
                <th className="border px-4 py-2">Courier</th>
                <th className="border px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sampleData.map((user) => (
                <tr key={user.id}>
                  <td className="border px-4 py-2">{user.first_name}</td>
                  <td className="border px-4 py-2">{user.last_name}</td>
                  <td className="border px-4 py-2">{user.email}</td>
                  <td className="border px-4 py-2">{couriers.find((c) => c.id === user.courier_id)?.username}</td>
                  <td className="border px-4 py-2">
                    <button onClick={() => handleEdit(user.id)} className="btn btn-sm">
                      Edit
                    </button>
                    <button
  onClick={() => {
    setCurrentUserId(user.id);
    setModalDeleteOpen(true);
  }}
  className="btn btn-sm btn-error"
>
  Delete
</button>

                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
      {modalOpen && (
  <dialog
    className="modal modal-open fixed inset-0 flex items-center justify-center bg-black/50"
    onClick={(e) => {
      if (e.target.nodeName === 'DIALOG') {
        setModalOpen(false);
      }
    }}
  >
    <form
      onSubmit={handleSubmit}
      className="modal-box bg-white p-6 rounded-lg shadow-lg w-full max-w-md"
      onClick={(e) => e.stopPropagation()} // Prevents closing when clicking inside the form
    >
      <h1 className="text-2xl font-bold text-gray-800 mb-4">
        {editMode ? 'Edit User' : 'Add User'}
      </h1>
      <div className='flex gap-4 w-full'>
      <div className="mb-4">
        <label className="block text-gray-600 mb-2">First Name</label>
        <input
          type="text"
          name="first_name"
          value={userData.first_name}
          onChange={handleChange}
          className="input input-bordered w-full"
          required
        />
      </div>
      <div className="mb-4">
        <label className="block text-gray-600 mb-2">Last Name</label>
        <input
          type="text"
          name="last_name"
          value={userData.last_name}
          onChange={handleChange}
          className="input input-bordered w-full"
          required
        />
      </div>
      </div>
      <div className="mb-4">
        <label className="block text-gray-600 mb-2">Email</label>
        <input
          type="email"
          name="email"
          value={userData.email}
          onChange={handleChange}
          className="input input-bordered w-full"
          required
        />
      </div>
      {!editMode ?       <div className="mb-4">
  <label className="block text-gray-600 mb-2">Password</label>
  <div className="relative w-full">
    <input
      type={showPassword ? 'text' : 'password'}
      name="password"
      value={userData.password}
      onChange={handleChange}
      className="input input-bordered w-full pr-12"
      required={!editMode}
    />
    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600"
    >
      {showPassword ? <FaEyeSlash /> : <FaEye />}
    </button>
  </div>
  <div className="flex mt-2 gap-4">
    <button type="button" onClick={handleGeneratePassword} className="btn">
      <FaDice /> Generate
    </button>
    <button type="button" onClick={handleCopyPassword} className="btn">
      <FaCopy /> Copy
    </button>
  </div>
</div> : null}

      <div className="mb-6">
        <label className="block text-gray-600 mb-2">Courier</label>
        <select
          name="courier_id"
          value={userData.courier_id}
          onChange={handleChange}
          className="select select-bordered w-full"
          required
        >
          <option value="" disabled>
            Select Courier
          </option>
          {couriers.map((courier) => (
            <option key={courier.id} value={courier.id}>
              {courier.username}
            </option>
          ))}
        </select>
      </div>
      <div className="flex justify-end gap-4">
        <button
          type="button"
          className="btn bg-gray-300 hover:bg-gray-400 text-gray-800"
          onClick={() => setModalOpen(false)}
        >
          Cancel
        </button>
        <button type="submit" className="btn bg-blue-500 hover:bg-blue-600 text-white">
          {editMode ? 'Update' : 'Add'} User
        </button>
      </div>
    </form>
  </dialog>
)}

{modalDeleteOpen && (
  <dialog
    className="modal modal-open fixed inset-0 flex items-center justify-center bg-black/50"
    onClick={(e) => {
      if (e.target.nodeName === 'DIALOG') {
        setModalDeleteOpen(false);
      }
    }}
  >
    <div
      className="modal-box bg-white p-6 rounded-lg shadow-lg w-full max-w-sm"
      onClick={(e) => e.stopPropagation()} // Prevents closing when clicking inside the modal
    >
      <h2 className="text-xl font-bold text-gray-800 mb-4">Confirm Delete</h2>
      <p className="text-gray-600 mb-6">
        Are you sure you want to delete this user? This action cannot be undone.
      </p>
      <div className="flex justify-end gap-4">
        <button
          className="btn bg-gray-300 hover:bg-gray-400 text-gray-800"
          onClick={() => setModalDeleteOpen(false)}
        >
          Cancel
        </button>
        <button
          className="btn bg-red-500 hover:bg-red-600 text-white"
          onClick={() => {
            handleDelete(currentUserId);
            setModalDeleteOpen(false);
          }}
        >
          Delete
        </button>
      </div>
    </div>
  </dialog>
)}


    </Layout>
  );
};

export default AddUsers;
